/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { Director, Stage, DefaultCaps, RenderConfig,
         FunctionalActor, CapNames, MaterialType,
         Events, Particles, PointCloud,
         Materials, DefaultNavigators, ObjectAddedEvent } from '@sick-davinci/glow';
import { Points, LineSegments, Vector3,
  BufferGeometry, BufferAttribute } from 'three';

const DIST = 1.2;
let va: Vector3;
let vb: Vector3;

const linemat = Materials.create({type: MaterialType.lineshader, params: {}});
linemat.transparent = true;

const pointmat = Materials.createPointMaterial({
  shape: 'circle',
  params: {
    size: 0.3,
    sizeAttenuation: true,
  },
});
pointmat.transparent = true;

const toSpherical = (arr: Array<number>): Array<number> => {
  const res = new Array<number>(arr.length);
  for (let i = 0; i < arr.length; i += 3) {
    const r = 3;
    const g = arr[i];
    const a = arr[i + 1];
    const b = arr[i + 2];

    va = new Vector3( Math.sin(a), Math.cos(a), 0.0 );
    vb = new Vector3( 0.0, Math.sin(b), Math.cos(b) );

    const n = new Vector3();
    n.crossVectors(va, vb);
    const ln = n.length();
    if ( ln < Number.EPSILON * 3) {
      n.set(1 , 0 , 0);
    } else {
      n.multiplyScalar(1 / ln);
    }

    vb.crossVectors(va, n).multiplyScalar(Math.cos(g) * r);
    va.multiplyScalar(Math.sin(g) * r);
    va.add(vb);

    res[i] = va.x;
    res[i + 1] = va.y;
    res[i + 2] = va.z;
  }
  return res;
};

/**
 * This is the Davinci Mission Demo!
 */
export const run = ((viewParent: HTMLElement, objFile, allowZoom: boolean = true) => {

  const globeName = 'globe';

  const renderConfig = new RenderConfig();
  renderConfig.enableAlpha = true;
  renderConfig.enableAntialiasing = true;
  const stage = new Stage(viewParent, {
    cameras: [
      {
        name: 'frontCamera',
        from: [10, 0, 0],
        to: [0, 0, 0],
        up: [0, 1, 0],
        fov: 40,
      },
    ],
    scenes: [
      { name: 'default' },
    ],
    viewports: [
      {
        scene: 'default',
        camera: 'frontCamera',
        navigator: 'orbit',
        left: 0.0,
        top: 0.0,
        width: 1.0,
        height: 1.0,
        bgcolor: 0x002266,
        alpha: 0.6,
      },
    ],
  }, renderConfig);

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit', {straightAhead: { enable: allowZoom }});

  // * Data handling using capabilities
  const director = new Director(stage);
  DefaultCaps.init(director);

  director.handle(CapNames.LIGHT, { payload: { lightType: 'point', color: 0xff8888 }, orientation: { position: [0, 10, 2] } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0x5599dd } });

  stage.addEventListener(Events.OBJECTADDED, ({detail}: CustomEvent<ObjectAddedEvent>) => {
    if (detail.name === globeName) {
      const obj = stage.getObjectByName(globeName);
      stage.addActor(new FunctionalActor(() => {
        obj.rotateY(0.004);
      }));
    }
  });

  director.handleCapData({
    capability: CapNames.OBJFILE,
    payload: { objFile },
    name: globeName,
    orientation: {
      rotation: [0, 0, 0],
      scale: [0.5, 0.5, 0.5],
      position: [0, 0, 0],
    },
    materials: [
      {
        type: MaterialType.basic, params:
          { wireframe: true, transparent: true, opacity: 0.6, color: 0xffffff },
      },
    ],
  });

  stage.start();

  const particles = new Particles(100);

  stage.addActor(new FunctionalActor(() => {
    particles.update();
    const arr = particles.getPositions();
    const arrSpherical = toSpherical(arr);
    const points = makePoints(arrSpherical);
    const lines = makeLines(arrSpherical);

    stage.applyThreeDirect((scene) => {
      scene.set(points);
      scene.set(lines);
    });
  }));

});

function makeLines(arr: Array<number>): LineSegments {
  const arrSeg = new Array<number>();
  const arrAlpha = new Array<number>();
  const pointLen = arr.length / 3;
  for (let i = 0; i < pointLen; i++) {
    const p1 = new Vector3(arr[i * 3], arr[i * 3 + 1], arr[i * 3 + 2]);
    for (let j = i + 1; j < pointLen; j++) {
      const p2 = new Vector3(arr[j * 3], arr[j * 3 + 1], arr[j * 3 + 2]);
      const dist = p1.distanceTo(p2);
      if (dist < DIST) {
        arrSeg.push(p1.x, p1.y, p1.z);
        arrSeg.push(p2.x, p2.y, p2.z);
        const c = Math.min((DIST - dist) / DIST * 2, 1.0);
        arrAlpha.push(c);
        arrAlpha.push(c);
      }
    }
  }

  const geo = new BufferGeometry();
  const vertices = Float32Array.from(arrSeg);
  geo.addAttribute('position', new BufferAttribute(vertices, 3));

  const alphas = Float32Array.from(arrAlpha);
  geo.addAttribute('alpha', new BufferAttribute(alphas, 1));

  const lines = new LineSegments(geo, linemat);
  lines.name = 'lines';
  lines.userData.disposeFlags = {
    disposeGeometry: true,
    disposeMaterial: false,
    disposeTexture: false,
  };
  return lines;
}

function makePoints(arrSpherical: Array<number>): Points {
  const geo = PointCloud.createFromArray(arrSpherical);
  const points = new Points(geo, pointmat);
  points.name = 'particles';
  points.userData.disposeFlags = {
    disposeGeometry: true,
    disposeMaterial: false,
    disposeTexture: false,
  };
  return points;
}
